import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, setCsrfToken, setUnauthorizedHandler } from './api';
import type { Identity } from './types';

interface AuthState {
  status: 'loading' | 'signed-in' | 'signed-out';
  identity: Identity | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [identity, setIdentity] = useState<Identity | null>(null);

  const clear = useCallback(() => {
    setIdentity(null);
    setCsrfToken(null);
    setStatus('signed-out');
  }, []);

  const adopt = useCallback((next: Identity) => {
    setIdentity(next);
    // Login regenerates the session id, so the token that comes back with the
    // identity is the only one that will work from here on.
    setCsrfToken(next.csrf_token);
    setStatus('signed-in');
  }, []);

  // A 401 from anywhere in the app lands here.
  useEffect(() => {
    setUnauthorizedHandler(clear);
    return () => setUnauthorizedHandler(null);
  }, [clear]);

  // Who is already signed in, if anyone.
  useEffect(() => {
    let cancelled = false;
    api
      .get<Identity>('/auth/me')
      .then((next) => {
        if (!cancelled) adopt(next);
      })
      .catch(() => {
        if (!cancelled) clear();
      });
    return () => {
      cancelled = true;
    };
  }, [adopt, clear]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      // Anonymous sessions get a CSRF token too, so the login POST itself is
      // protected. Fetch one before posting.
      await api.refreshCsrfToken();
      const next = await api.post<Identity>('/auth/login', { email, password });
      adopt(next);
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post<void>('/auth/logout');
    } catch (error) {
      // Already gone server side is a fine outcome for a logout.
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
    } finally {
      clear();
    }
  }, [clear]);

  const can = useCallback(
    (permission: string) => identity?.permissions.includes(permission) ?? false,
    [identity],
  );

  const value = useMemo<AuthState>(
    () => ({ status, identity, signIn, signOut, can }),
    [status, identity, signIn, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
